import { useEffect, useRef, useState } from 'react';

import { Input, Modal } from 'antd';
import { LoaderCircle, Ticket } from 'lucide-react';

import PayQRCodes from '../../../components/PayQRCodes';
import { redeemCode } from '../../../components/PayQRCodes/usePayOrder';
import { useDevice } from '../../../hooks/useDevice';
import { REDEEM_CODE_MAX } from '../constants';
import { errorMessageOf, formatPrice } from '../helpers';

import styles from '../index.module.scss';

/**
 * 答辩PPT 付款弹窗：同一个 payment.orderNo 的支付宝 / 微信付款码，或用兑换码 / 套餐抵扣。
 * 到账由 usePayOrder 轮询（父组件持有），兑换成功后回调 onRedeemed。
 */
export default function PayModal({
    open,
    payment,
    subtitle,
    queued,
    channels,
    onRetryChannel,
    redeemPrefill,
    onRedeemed,
    checking,
    onCheck,
    onClose,
}) {
    const { isMobile } = useDevice();
    const [code, setCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [error, setError] = useState('');
    const busy = useRef(false);
    const orderNo = payment?.orderNo || '';
    const price = formatPrice(payment?.price);

    useEffect(() => {
        if (!open) return;
        setCode(redeemPrefill || '');
        setError('');
        setRedeeming(false);
        busy.current = false;
    }, [open, orderNo, redeemPrefill]);

    const handleRedeem = async () => {
        const normalized = code.trim();
        if (busy.current) return;
        if (!normalized) {
            setError('请输入答辩PPT兑换码或金额码');
            return;
        }
        if (!orderNo) {
            setError('订单不存在，请关闭后重新确认生成');
            return;
        }
        busy.current = true;
        setRedeeming(true);
        setError('');
        try {
            await redeemCode(orderNo, normalized);
            onRedeemed?.(normalized);
        } catch (err) {
            setError(errorMessageOf(err, '兑换失败，请检查兑换码'));
        } finally {
            busy.current = false;
            setRedeeming(false);
        }
    };

    const wechat = channels?.wechat || {};

    return (
        <Modal
            open={open}
            title="支付后开始生成答辩PPT"
            footer={null}
            width={isMobile ? 'calc(100% - 24px)' : 520}
            centered
            maskClosable={false}
            closable={!redeeming}
            onCancel={redeeming ? undefined : onClose}
            className={styles.payModal}
        >
            <div className={styles.payHead}>
                <div className={styles.payPrice}>
                    <span>应付金额</span>
                    <strong>{price || '—'}</strong>
                </div>
                {subtitle ? <p className={styles.paySubtitle}>{subtitle}</p> : null}
            </div>

            {queued ? (
                <p className={styles.confirmHint}>
                    资料还在整理中，支付后可以离开页面，整理完成会自动开始生成。
                </p>
            ) : null}

            <div className={styles.payCodes}>
                <PayQRCodes
                    alipay={channels?.alipay}
                    wechat={wechat}
                    onRetry={onRetryChannel}
                    size={isMobile ? 120 : 150}
                />
                {isMobile && wechat.mwebUrl ? (
                    <a className={styles.textButton} href={wechat.mwebUrl}>
                        在手机上直接打开微信支付
                    </a>
                ) : null}
            </div>

            <div className={styles.payDivider}>
                <span>或</span>
            </div>

            <div className={styles.redeemBox}>
                <label htmlFor="ppt-redeem-code">
                    <Ticket size={15} aria-hidden />
                    使用兑换码 / 套餐
                </label>
                <div className={styles.redeemRow}>
                    <Input
                        id="ppt-redeem-code"
                        value={code}
                        maxLength={REDEEM_CODE_MAX}
                        allowClear
                        disabled={redeeming}
                        placeholder="答辩PPT兑换码（MLP 开头）或金额码"
                        onChange={(event) => {
                            setCode(event.target.value);
                            if (error) setError('');
                        }}
                        onPressEnter={handleRedeem}
                    />
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        disabled={redeeming || !code.trim()}
                        onClick={handleRedeem}
                    >
                        {redeeming ? (
                            <LoaderCircle className={styles.spin} size={15} aria-hidden />
                        ) : null}
                        {redeeming ? '兑换中…' : '兑换'}
                    </button>
                </div>
                {error ? (
                    <p className={styles.redeemError} role="alert">
                        {error}
                    </p>
                ) : (
                    <small>写作订单附带的答辩PPT兑换码，可在「订单与下载」里查看。</small>
                )}
            </div>

            <div className={styles.payFoot}>
                <p>
                    支付完成后自动开始生成，无需刷新。关闭弹窗不会取消订单，之后可点「去支付」继续。
                </p>
                <button
                    type="button"
                    className={styles.textButton}
                    disabled={checking}
                    onClick={onCheck}
                >
                    {checking ? '正在查询…' : '我已支付'}
                </button>
            </div>
        </Modal>
    );
}
